import { useContext, useMemo } from "react"
import { utils } from "@rjsf/core"
import { JSONSchema7 } from "json-schema"
import { Chip, Markdown, Text, Title } from "dash-styled"
import { SchemaForm, Table, ToolsContext } from "resha"

import { SuperTaskParametersModel } from "../../../../../../../../../../../../Models/API/SuperTaskModel"
import { StaticAccordion } from "../../../../../../../../../Common/StaticAccordion/StaticAccordion"

type TaskParametersProps = {
    schema: JSONSchema7
    parameters: SuperTaskParametersModel
    disabled?: boolean
    onChange: (parameters: SuperTaskParametersModel) => void
}

const TaskParameters = ({
    schema,
    parameters,
    disabled = false,
    onChange
}: TaskParametersProps) => {
    const tools = useContext(ToolsContext)

    const items = useMemo(
        () =>
            Object.entries(schema.properties || {}).map(([name, property]) => {
                const resolvedSchema = utils.resolveSchema(property, schema)
                    ?.allOf?.[0] as JSONSchema7
                return {
                    name,
                    title: (property as JSONSchema7).title,
                    description: resolvedSchema?.description,
                    type: resolvedSchema?.type,
                    value: parameters[name],
                    schema: {
                        ...resolvedSchema,
                        title: "",
                        description: ""
                    }
                }
            }),
        [parameters, schema]
    )

    const handleChange = (name: string) => (value: unknown) => {
        if (!disabled) {
            const newParams = { ...parameters, [name]: value }
            onChange(newParams)
        }
    }

    return (
        <StaticAccordion
            withFooter
            title="Parameters"
            titleComponent="h5"
            className=" mb-8 shadow-lg"
            bodyClassName="pt-2 px-4 bg-white rounded-b-large shadow-inner"
        >
            <Table
                className="w-full"
                rowBgColor=""
                withBorders={false}
                style={{
                    borderSpacing: "0 4px"
                }}
                getKey={({ name }) => name}
                items={items}
                columns={[
                    {
                        className: "w-1/12 pl-2 border-b-transparent",
                        renderHeader: () => null,
                        render: () => null
                    },
                    {
                        className: "w-5/12 border-b-transparent",
                        renderHeader: () => (
                            <Text color="gray" className="m-0">
                                Name & Description
                            </Text>
                        ),
                        render: ({ title, description }) => {
                            return (
                                <>
                                    <Title
                                        component="h6"
                                        variant="heading4"
                                        className="capitalize m-0"
                                    >
                                        {title}
                                    </Title>
                                    {description && (
                                        <Markdown
                                            className="text-sm text-smoke-500 mb-1"
                                            text={description}
                                        />
                                    )}
                                </>
                            )
                        }
                    },
                    {
                        className: "w-2/12 border-b-transparent",
                        renderHeader: () => (
                            <Text color="gray" className="m-0">
                                Type
                            </Text>
                        ),
                        render: ({ type }) => {
                            return <Text>{type}</Text>
                        }
                    },
                    {
                        className: "w-4/12 border-b-transparent",
                        renderHeader: () => (
                            <Text color="gray" className="m-0">
                                Value
                            </Text>
                        ),
                        render: ({ name, value, schema }) => {
                            return disabled ? (
                                Array.isArray(value) ? (
                                    <>
                                        {value.slice(0, 3).map((el, index) => (
                                            <Chip
                                                key={`${el}_${index}`}
                                                size="md"
                                                color="blueviolet"
                                                className="mr-2"
                                            >
                                                {el}
                                            </Chip>
                                        ))}
                                        {value.length > 3 &&
                                            `${value.length - 3} more...`}
                                    </>
                                ) : (
                                    value
                                )
                            ) : (
                                <SchemaForm
                                    {...tools}
                                    schema={schema}
                                    uiSchema={{
                                        "ui:enumDisabled": ["PRIORITY"]
                                    }}
                                    formData={value}
                                    onChange={handleChange(name)}
                                />
                            )
                        }
                    }
                ]}
            />
        </StaticAccordion>
    )
}

export default TaskParameters
